import React, { useMemo, useState, useEffect } from 'react'

/**
 * COMPONENTE: KISS CAM OVERLAY (VISUAL CHILD)
 * ------------------------------------------------------------------
 * Renderiza el banner del beso con marcos vectoriales neón y el
 * visor de corazón SVG con aceleración 3D en la GPU.
 * ------------------------------------------------------------------
 */
const Kiss = ({ 
    config, 
    tensionActive = false, 
    heartTensionActive = false, 
    successActive = false, 
    failActive = false, 
    destelloActive = false
}) => {
    const {
        visible,
        titleText,
        failText,
        frameStyle, // 'classic-red', 'double-gold', 'cyber-glow', 'none'
        frameColor,
        borderWidth,
        frameZoom,
        lowerThirdBgColor,
        lowerThirdBgOpacity,
        screenFilter, // 'none', 'sepia', 'pink-romantic', 'grayscale'
        floatingShape, // 'hearts', 'lips', 'broken-hearts'
        shapeOpacity,
        shapeGlow,
        logoDataUrl,
        logoCorner, // 'top-left', 'top-right', 'bottom-left', 'bottom-right'
        logoScale,
        scale,
        titleSize,
        offsetX,
        offsetY
    } = config;

    const rgbFrame = frameColor || '#ff0033';
    
    // Dimensiones de pantalla para reescalado matemático del corazón
    const [dimensions, setDimensions] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });

    useEffect(() => {
        const handleResize = () => {
            setDimensions({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const { width, height } = dimensions;
    const s = Math.min(width, height) * 0.040;
    const tx = (width / 2) - (12 * s);
    const ty = (height / 2) - (11.5 * s);
    const strokeWidthVal = 10 / s;

    // Clases dinámicas
    const containerClasses = `kiss-overlay-container ${visible ? 'reveal' : 'hide-overlay'} filter-${screenFilter} ${tensionActive ? 'state-tension' : ''} ${heartTensionActive ? 'state-heart-tension' : ''} ${failActive ? 'state-fail' : ''}`;

    const cssVariablesStyle = {
        '--frame-color': rgbFrame,
        '--border-width': `${borderWidth || 6}px`,
        '--frame-zoom': `${frameZoom || 92}%`,
        '--lt-bg': lowerThirdBgColor || '#000000',
        '--lt-opacity': lowerThirdBgOpacity ?? 0.6,
        '--shape-opacity': shapeOpacity ?? 0.3,
        '--shape-glow': `${shapeGlow || 15}px`,
        '--kiss-scale': scale || 1.0,
        '--title-size': `${titleSize || 3.5}rem`,
        '--offset-x': `${offsetX || 0}px`,
        '--offset-y': `${offsetY || 0}px`,
        '--logo-scale': logoScale || 1.0
    };

    // Render de figuras flotantes continuas
    const getShapeSvg = (type) => {
        switch (type) {
            case 'hearts':
                return (
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                );
            case 'lips':
                return (
                    <svg viewBox="0 0 100 100" fill="currentColor">
                        <path d="M10 50 C20 40, 40 30, 50 45 C60 30, 80 40, 90 50 C80 65, 60 70, 50 55 C40 70, 20 65, 10 50 Z M15 50 C30 58, 45 52, 50 47 C55 52, 70 58, 85 50 C70 48, 55 45, 50 47 C45 45, 30 48, 15 50 Z" />
                    </svg>
                );
            case 'broken-hearts':
                return (
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09 C12 5.5 12 6.5 11.5 7 L13 9.5 L11 12.5 L13.5 15.5 L12 18 L13 21.35 Z" />
                    </svg>
                );
            default:
                return null;
        }
    };

    // Pool de 10 figuras flotantes
    const floatingElements = useMemo(() => {
        const list = [];
        for (let i = 0; i < 10; i++) {
            list.push({
                id: i,
                left: Math.random() * 90 + 5,
                delay: Math.random() * 8,
                duration: Math.random() * 5 + 7,
                size: Math.random() * 30 + 20
            });
        }
        return list;
    }, [floatingShape]);

    // Lluvia constante de corazones (Punto 1)
    const rainHearts = useMemo(() => {
        const list = [];
        for (let i = 0; i < 30; i++) {
            list.push({
                id: i,
                left: Math.random() * 100,
                delay: Math.random() * -15, // Delay negativo para empezar de inmediato
                duration: Math.random() * 8 + 7,
                size: Math.random() * 20 + 10
            });
        }
        return list;
    }, []);

    // Corazones ambientales flotando en los costados para el Visor (Punto 5)
    const heartTensionParticles = useMemo(() => {
        const list = [];
        const colorsPool = ['#ff0055', '#ff1493', '#ff3333', '#ff99cc', '#ffd700', '#ffffff'];
        for (let i = 0; i < 75; i++) {
            let leftPercent;
            let topPercent;
            const zone = i % 10;
            if (zone === 8) { // Top (10%)
                leftPercent = Math.random() * 100;
                topPercent = Math.random() * 15;
            } else if (zone === 9) { // Bottom (10%)
                leftPercent = Math.random() * 100;
                topPercent = 85 + Math.random() * 15;
            } else if (zone < 4) { // Left (40%)
                leftPercent = Math.random() * 25;
                topPercent = Math.random() * 100;
            } else { // Right (40%)
                leftPercent = 75 + Math.random() * 25;
                topPercent = Math.random() * 100;
            }

            const direction = (i % 2 === 0) ? 'float-up' : 'float-down';

            list.push({
                id: i,
                left: leftPercent,
                top: topPercent,
                color: colorsPool[i % colorsPool.length],
                delay: Math.random() * -12,
                duration: Math.random() * 7 + 4,
                size: Math.random() * 18 + 12,
                direction
            });
        }
        return list;
    }, []);

    // Corazones que brotan del contorno del marco del visor central (Modo 3)
    const heartVisorOutlineParticles = useMemo(() => {
        const list = [];
        const count = 35;
        const screenW = window.innerWidth;
        const screenH = window.innerHeight;
        
        const sVal = Math.min(screenW, screenH) * 0.040;
        const centerX = screenW / 2;
        const centerY = screenH / 2;

        for (let i = 0; i < count; i++) {
            const t = (i / count) * Math.PI * 2;
            const xVal = 16 * Math.pow(Math.sin(t), 3);
            const yVal = -(13 * Math.cos(t) - 5 * Math.cos(2*t) - 2 * Math.cos(3*t) - Math.cos(4*t));
            
            const scaleFactor = sVal * 0.8;
            const px = centerX + xVal * scaleFactor;
            const py = centerY + yVal * scaleFactor - (sVal * 2);

            list.push({
                id: i,
                left: px,
                top: py,
                delay: Math.random() * -10,
                duration: Math.random() * 5 + 4,
                size: Math.random() * 16 + 8
            });
        }
        return list;
    }, [width, height]);

    // Partículas de explosiones radiales interrumpibles y graduales
    const [explosionParticles, setExplosionParticles] = useState([]);

    useEffect(() => {
        if (!successActive && !failActive && !destelloActive) {
            setExplosionParticles([]);
            return;
        }

        const tempParticles = [];
        const screenW = window.innerWidth;
        const screenH = window.innerHeight;
        
        let charPool = [];
        let count = 40;
        let pColor = '#ff0033';
        let animDuration = 1.6;

        if (successActive) {
            charPool = ['💋', '❤️', '💋', '💕'];
            count = 65;
            animDuration = config.successSpeed ?? 1.6;
        } else if (failActive) {
            charPool = ['👎'];
            count = 55;
            pColor = '#ef4444';
            animDuration = config.failSpeed ?? 1.6;
        } else if (destelloActive) {
            charPool = ['❤️', '💖', '💛', '💕', '✨'];
            count = 55;
            animDuration = config.destelloSpeed ?? 1.6;
        }

        for (let i = 0; i < count; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = Math.random() * 320 + 80;
            const endSpeed = Math.random() * 550 + 250;
            const size = Math.random() * 2.8 + 1.2;

            // Retraso gradual escalonado
            const delay = (i / count) * (animDuration * 0.65) + Math.random() * 0.1;

            tempParticles.push({
                id: i,
                x: screenW / 2,
                y: screenH / 2,
                tx: Math.cos(angle) * speed,
                ty: Math.sin(angle) * speed,
                txEnd: Math.cos(angle) * endSpeed,
                tyEnd: Math.sin(angle) * endSpeed,
                char: charPool[i % charPool.length],
                color: pColor,
                size: `${size}rem`,
                duration: `${animDuration}s`,
                delay: `${delay}s`
            });
        }
        setExplosionParticles(tempParticles);
    }, [successActive, failActive, destelloActive, config.successSpeed, config.failSpeed, config.destelloSpeed]);

    // Popups laterales de "¡BESO!" para Modo 3 (Punto 5)
    const [localBesoPopups, setLocalBesoPopups] = useState([]);

    useEffect(() => {
        if (!heartTensionActive) {
            setLocalBesoPopups([]);
            return;
        }

        let nextBesoIsLeft = Math.random() > 0.5;
        const interval = setInterval(() => {
            const screenW = window.innerWidth;
            const screenH = window.innerHeight;
            const isLeft = nextBesoIsLeft;
            nextBesoIsLeft = !nextBesoIsLeft;

            const minX = isLeft ? (screenW * 0.05) : (screenW * 0.68);
            const rangeX = screenW * 0.18;
            const randX = minX + Math.random() * rangeX;
            const randY = screenH * 0.2 + Math.random() * (screenH * 0.5);
            const rot = (Math.random() - 0.5) * 50;
            const id = Date.now() + Math.random();

            setLocalBesoPopups(prev => [...prev, { id, x: randX, y: randY, rot }]);

            setTimeout(() => {
                setLocalBesoPopups(prev => prev.filter(p => p.id !== id));
            }, 2000);
        }, 1600);

        return () => clearInterval(interval);
    }, [heartTensionActive]);

    // SVG Mascara para corte del Corazón en Modo 3
    const heartSvgMask = (
        <svg id="heart-cutout-overlay">
            <defs>
                <mask id="heart-hole-visual">
                    <rect width="100%" height="100%" fill="white" />
                    <path 
                        d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" 
                        fill="black" 
                        transform={`translate(${tx}, ${ty}) scale(${s})`}
                    />
                </mask>
                <radialGradient id="darkOverlayGradVisual" cx="50%" cy="50%" r="70%">
                    <stop offset="25%" stop-color="#2a0011" stop-opacity="0.80" />
                    <stop offset="100%" stop-color="#000000" stop-opacity="0.95" />
                </radialGradient>
            </defs>
            <rect width="100%" height="100%" fill="url(#darkOverlayGradVisual)" mask="url(#heart-hole-visual)"/>
            {/* Contorno brillante alrededor del corazón */}
            <path 
                className="pulse-heart-path" 
                d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" 
                fill="none" 
                stroke="var(--frame-color)" 
                strokeWidth={strokeWidthVal} 
                strokeLinejoin="round" 
                transform={`translate(${tx}, ${ty}) scale(${s})`}
            />
        </svg>
    );

    return (
        <div className={containerClasses} style={cssVariablesStyle}>
            <style>{`
                .kiss-overlay-container {
                    position: absolute;
                    top: 0; left: 0;
                    width: 100vw; height: 100vh;
                    overflow: hidden;
                    pointer-events: none;
                    background-color: transparent !important;
                    transition: filter 1s ease-in-out, opacity 0.8s ease;
                    z-index: 10;
                }

                .kiss-overlay-container.hide-overlay {
                    opacity: 0;
                }

                /* FILTROS DE CÁMARA (ROMÁNTICO) */
                .filter-sepia { filter: sepia(0.6) contrast(1.1); }
                .filter-pink-romantic { filter: hue-rotate(320deg) brightness(1.05) saturate(1.2); }
                .filter-grayscale { filter: grayscale(0.85) contrast(1.2); }

                /* 1. VISOR SVG CORAZÓN */
                #heart-cutout-overlay {
                    position: absolute;
                    top: 0; left: 0;
                    width: 100vw; height: 100vh;
                    z-index: 4;
                    opacity: 0;
                    transform: scale(2.8);
                    transition: opacity 1s cubic-bezier(0.2, 0.8, 0.2, 1), transform 1.2s cubic-bezier(0.2, 0.8, 0.2, 1);
                }

                .state-heart-tension #heart-cutout-overlay {
                    opacity: 1;
                    transform: scale(1);
                }

                .pulse-heart-path {
                    filter: drop-shadow(0 0 10px var(--frame-color));
                }

                /* 2. BARRAS CINEMATOGRÁFICAS (MODO 4) */
                .kiss-vignette {
                    position: absolute;
                    top: 0; left: 0; right: 0; bottom: 0;
                    box-shadow: 0 0 0px rgba(0,0,0,0) inset;
                    z-index: 2;
                    transition: box-shadow 0.8s ease-in-out;
                }

                .state-tension .kiss-vignette {
                    box-shadow: 0 0 250px rgba(180, 0, 30, 0.8) inset;
                    animation: kissVignettePulse 1s infinite alternate;
                }

                @keyframes kissVignettePulse {
                    0% { box-shadow: 0 0 200px rgba(130,0,20,0.7) inset; }
                    100% { box-shadow: 0 0 300px rgba(220,0,40,0.9) inset; }
                }

                .cinema-bar {
                    position: absolute;
                    left: 0; width: 100%; height: 70px;
                    background: #000000;
                    z-index: 4;
                    transition: transform 0.8s cubic-bezier(0.85, 0, 0.15, 1);
                }
                .cinema-bar-top { top: 0; transform: translateY(-100%); }
                .cinema-bar-bottom { bottom: 0; transform: translateY(100%); }

                .state-tension .cinema-bar-top { transform: translateY(0); }
                .state-tension .cinema-bar-bottom { transform: translateY(0); }

                /* 3. MARCOS CIBERNÉTICOS (MARCO ELEGANTE) */
                .kiss-elegant-frame {
                    position: absolute;
                    top: 4%; left: 4%; right: 4%; bottom: 4%;
                    border: var(--border-width) solid rgba(255, 0, 50, 0.35);
                    border-radius: 12px;
                    z-index: 3;
                    transition: opacity 0.5s ease;
                }

                .style-double-gold .kiss-elegant-frame {
                    border: var(--border-width) double #ffd700;
                    box-shadow: 0 0 15px rgba(255,215,0,0.3);
                }

                .style-cyber-glow .kiss-elegant-frame {
                    border: var(--border-width) solid var(--frame-color);
                    box-shadow: 0 0 20px var(--frame-color), inset 0 0 20px var(--frame-color);
                }

                .style-none .kiss-elegant-frame {
                    display: none;
                }

                /* Esquinas decorativas del marco clásico */
                .style-classic-red .kiss-elegant-frame::before,
                .style-classic-red .kiss-elegant-frame::after {
                    content: '';
                    position: absolute;
                    width: 50px; height: 50px;
                    border: 8px solid var(--frame-color);
                }
                .style-classic-red .kiss-elegant-frame::before {
                    top: -8px; left: -8px;
                    border-right: none; border-bottom: none;
                }
                .style-classic-red .kiss-elegant-frame::after {
                    bottom: -8px; right: -8px;
                    border-left: none; border-top: none;
                }

                /* Ocultar marcos durante tensiones */
                .state-tension .kiss-elegant-frame,
                .state-heart-tension .kiss-elegant-frame {
                    opacity: 0;
                }

                /* 4. FIGURAS FLOTANTES CONTINUAS */
                .kiss-floating-shape {
                    position: absolute;
                    bottom: -60px;
                    color: var(--frame-color);
                    opacity: var(--shape-opacity);
                    filter: drop-shadow(0 0 var(--shape-glow) var(--frame-color));
                    z-index: 5;
                    will-change: transform;
                    animation: floatKissUp linear infinite;
                }

                @keyframes floatKissUp {
                    0% { transform: translateY(0) scale(0.6) rotate(0deg); opacity: 0; }
                    20% { opacity: var(--shape-opacity); }
                    85% { opacity: var(--shape-opacity); }
                    100% { transform: translateY(-110vh) scale(1.3) rotate(360deg); opacity: 0; }
                }

                /* Desactivar partículas si tensión o abucheo activo */
                .state-tension .kiss-floating-shape,
                .state-heart-tension .kiss-floating-shape,
                .state-fail .kiss-floating-shape {
                    display: none;
                }

                /* 5. LOWER THIRD (TEXTOS DE CONTROL) */
                .kiss-lower-third {
                    position: absolute;
                    bottom: 60px;
                    left: 50%;
                    transform: translate(-50%, var(--offset-y)) scale(var(--kiss-scale));
                    transform-origin: bottom center;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 12;
                    background: linear-gradient(90deg, transparent 0%, var(--lt-bg) 50%, transparent 100%);
                    padding: 12px 60px;
                    border-radius: 40px;
                    transition: bottom 0.8s cubic-bezier(0.85, 0, 0.15, 1);
                    will-change: transform, opacity;
                }

                .state-tension .kiss-lower-third {
                    bottom: 90px;
                }

                .state-heart-tension .kiss-lower-third {
                    opacity: 0;
                }

                .kiss-lower-text {
                    font-family: 'Montserrat', sans-serif;
                    font-size: var(--title-size);
                    font-weight: 900;
                    letter-spacing: 6px;
                    color: #ffffff;
                    text-transform: uppercase;
                    text-shadow: 0 0 15px var(--frame-color);
                    margin: 0;
                    white-space: nowrap;
                }

                /* Corazón central latiendo */
                .kiss-heart-pulse-icon {
                    width: 55px; height: 55px;
                    margin: 0 20px;
                    color: var(--frame-color);
                    filter: drop-shadow(0 0 12px var(--frame-color));
                    animation: heartBeatSoft 1.5s infinite ease-in-out;
                    will-change: transform;
                }

                @keyframes heartBeatSoft {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.15); }
                }

                /* Latido tenso durante abucheo/tensión */
                .state-tension .kiss-heart-pulse-icon {
                    animation: heartBeatTense 0.4s infinite;
                }

                @keyframes heartBeatTense {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.3) rotate(5deg); }
                }

                /* 6. LOGOTIPO PATROCINADOR DE ESQUINAS */
                .kiss-corner-logo {
                    position: absolute;
                    max-width: 130px;
                    max-height: 80px;
                    z-index: 15;
                    object-fit: contain;
                    transition: opacity 0.5s ease-in-out;
                }

                .corner-top-left { top: 35px; left: 35px; }
                .corner-top-right { top: 35px; right: 35px; }
                .corner-bottom-left { bottom: 35px; left: 35px; }
                .corner-bottom-right { bottom: 35px; right: 35px; }

                /* Ocultar patrocinadores en Modo 4 */
                .state-tension .kiss-corner-logo {
                    opacity: 0;
                }

                /* EMOJI PARTICULAS (GOLPES) */
                .kiss-particle-item {
                    position: absolute;
                    font-size: 2.2rem;
                    z-index: 22;
                    pointer-events: none;
                    animation: popKissParticle 1.6s ease-out forwards;
                    will-change: transform, opacity;
                }

                @keyframes popKissParticle {
                    0% { opacity: 0; transform: translate3d(0,0,0) scale(0.2); }
                    15% { opacity: 1; transform: translate3d(var(--tx), var(--ty), 0) scale(1.3); }
                    100% { opacity: 0; transform: translate3d(var(--tx-end), var(--ty-end), 0) scale(0.6); }
                }

                /* SPAM BESO POPUPS (STATE 3) */
                .kiss-beso-popup-text {
                    position: absolute;
                    font-family: 'Montserrat', sans-serif;
                    font-size: 4rem;
                    font-weight: 900;
                    color: #ffffff;
                    text-transform: uppercase;
                    text-shadow: 0 0 15px var(--frame-color), 0 0 25px var(--frame-color);
                    animation: besoPopupAnim 1.8s ease-in-out forwards;
                    z-index: 30;
                    will-change: transform, opacity;
                }

                @keyframes besoPopupAnim {
                    0% { opacity: 0; transform: scale(0.4) rotate(var(--rot)); }
                    20% { opacity: 1; transform: scale(1.1) rotate(var(--rot)); }
                    80% { opacity: 1; transform: scale(1) rotate(var(--rot)); }
                    100% { opacity: 0; transform: scale(1.3) rotate(var(--rot)); }
                }

                .kiss-bg-rain-container {
                    position: absolute;
                    top: 0; left: 0;
                    width: 100vw; height: 100vh;
                    overflow: hidden;
                    z-index: 1;
                    pointer-events: none;
                    transition: opacity 0.5s ease;
                }

                .state-heart-tension .kiss-bg-rain-container,
                .state-heart-tension .kiss-rain-heart,
                .state-heart-tension .kiss-floating-shape {
                    display: none !important;
                    opacity: 0 !important;
                    visibility: hidden !important;
                }

                .kiss-ambient-heart svg,
                .kiss-floating-shape svg,
                .kiss-visor-outline-heart svg,
                .kiss-rain-heart svg {
                    width: 100% !important;
                    height: 100% !important;
                    display: block !important;
                }

                .kiss-rain-heart {
                    position: absolute;
                    top: -10%;
                    color: var(--frame-color);
                    opacity: 0.14;
                    filter: drop-shadow(0 0 5px var(--frame-color));
                    animation: fall-rain-heart linear infinite;
                    pointer-events: none;
                }

                @keyframes fall-rain-heart {
                    0% { transform: translateY(0) rotate(0deg); }
                    100% { transform: translateY(115vh) rotate(360deg); }
                }

                @keyframes float-vibe-up {
                    0% { transform: translateY(0) scale(0.9) rotate(0deg); opacity: 1; }
                    50% { transform: translateY(-50vh) scale(1.18) rotate(15deg); opacity: 1; filter: drop-shadow(0 0 12px currentColor); }
                    100% { transform: translateY(-110vh) scale(0.85) rotate(-15deg); opacity: 0.15; }
                }

                @keyframes float-vibe-down {
                    0% { transform: translateY(0) scale(0.9) rotate(0deg); opacity: 1; }
                    50% { transform: translateY(50vh) scale(1.18) rotate(-15deg); opacity: 1; filter: drop-shadow(0 0 12px currentColor); }
                    100% { transform: translateY(110vh) scale(0.85) rotate(15deg); opacity: 0.15; }
                }

                .kiss-visor-outline-heart {
                    position: absolute;
                    color: var(--frame-color);
                    opacity: 0.7;
                    filter: drop-shadow(0 0 5px var(--frame-color));
                    animation: float-up-visor-heart linear infinite;
                    pointer-events: none;
                    z-index: 10;
                }

                @keyframes float-up-visor-heart {
                    0% { transform: translateY(0) scale(0.8); opacity: 0.8; }
                    50% { opacity: 0.6; }
                    100% { transform: translateY(-120px) scale(0.4); opacity: 0; }
                }
            `}</style>

            {/* Lluvia constante de corazones (Punto 1) */}
            <div className="kiss-bg-rain-container">
                {rainHearts.map(h => (
                    <div 
                        key={h.id}
                        className="kiss-rain-heart"
                        style={{
                            left: `${h.left}%`,
                            animationDelay: `${h.delay}s`,
                            animationDuration: `${h.duration}s`,
                            width: `${h.size}px`,
                            height: `${h.size}px`
                        }}
                    >
                        {getShapeSvg('hearts')}
                    </div>
                ))}
            </div>

            {/* Corazones flotantes para Visor Corazón (Punto 5) */}
            {heartTensionActive && (
                <div className="kiss-heart-tension-ambient">
                    {heartTensionParticles.map(p => (
                        <div 
                            key={p.id}
                            className="kiss-ambient-heart"
                            style={{
                                left: `${p.left}%`,
                                top: `${p.top}%`,
                                animationDelay: `${p.delay}s`,
                                animationDuration: `${p.duration}s`,
                                width: `${p.size}px`,
                                height: `${p.size}px`,
                                position: 'absolute',
                                color: p.color,
                                opacity: 0.85,
                                filter: `drop-shadow(0 0 8px ${p.color})`,
                                animation: p.direction === 'float-up' ? 'float-vibe-up linear infinite' : 'float-vibe-down linear infinite',
                                pointerEvents: 'none'
                            }}
                        >
                            {getShapeSvg('hearts')}
                        </div>
                    ))}
                </div>
            )}

            {/* Máscara de corte de corazón */}
            {heartSvgMask}

            {/* Barras cinematográficas y viñeta */}
            <div className="cinema-bar cinema-bar-top" />
            <div className="cinema-bar cinema-bar-bottom" />
            <div className="kiss-vignette" />

            {/* Borde elegante */}
            <div className="kiss-elegant-frame" />

            {/* Figuras flotantes continuas */}
            {floatingElements.map(el => (
                <div
                    key={el.id}
                    className="kiss-floating-shape"
                    style={{
                        width: `${el.size}px`,
                        height: `${el.size}px`,
                        left: `${el.left}%`,
                        animationDuration: `${el.duration}s`,
                        animationDelay: `${el.delay}s`
                    }}
                >
                    {getShapeSvg(floatingShape)}
                </div>
            ))}

            {/* Logotipos en esquinas */}
            {logoDataUrl ? (
                <img 
                    src={logoDataUrl} 
                    alt="Logo Patrocinador" 
                    className={`kiss-corner-logo corner-${logoCorner}`}
                    style={{ transform: `scale(${logoScale})` }} 
                />
            ) : (
                <>
                    <img 
                        src="/img/overlays/kiss-cam/1.png" 
                        alt="Logo Light" 
                        className={`kiss-corner-logo corner-${logoCorner}`}
                        style={{ 
                            transform: `scale(${logoScale})`,
                            opacity: heartTensionActive ? 0 : 1 
                        }} 
                    />
                    <img 
                        src="/img/overlays/kiss-cam/1O.png" 
                        alt="Logo Dark" 
                        className={`kiss-corner-logo corner-${logoCorner}`}
                        style={{ 
                            transform: `scale(${logoScale})`,
                            opacity: heartTensionActive ? 1 : 0 
                        }} 
                    />
                </>
            )}

            {/* Lower Third (Información en vivo) */}
            <div className="kiss-lower-third">
                {!failActive ? (
                    <div className="d-flex align-items-center">
                        <span className="kiss-lower-text">{titleText.split(' ')[0]}</span>
                        <div className="kiss-heart-pulse-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                            </svg>
                        </div>
                        <span className="kiss-lower-text">{titleText.split(' ')[1] || 'CAM'}</span>
                    </div>
                ) : (
                    <div className="d-flex align-items-center" style={{ color: '#ffcccc' }}>
                        <span style={{ fontSize: '2.5rem', filter: 'drop-shadow(0 0 10px red)' }}>👎</span>
                        <span className="kiss-lower-text mx-4" style={{ textShadow: '0 0 15px red' }}>{failText}</span>
                        <span style={{ fontSize: '2.5rem', filter: 'drop-shadow(0 0 10px red)' }}>👎</span>
                    </div>
                )}
            </div>

            {/* Partículas de explosión (Corazones o thumbs down) */}
            {explosionParticles.map(p => (
                <div 
                    key={p.id}
                    className="kiss-particle-item"
                    style={{
                        left: `${p.x}px`,
                        top: `${p.y}px`,
                        color: p.color,
                        fontSize: p.size,
                        '--tx': `${p.tx}px`,
                        '--ty': `${p.ty}px`,
                        '--tx-end': `${p.txEnd}px`,
                        '--ty-end': `${p.tyEnd}px`,
                        animationDuration: p.duration,
                        animationDelay: `${p.delay}s`
                    }}
                >
                    {p.char}
                </div>
            ))}
            {/* Beso Popups (Modo 3) */}
            {localBesoPopups.map(b => (
                <div 
                    key={b.id}
                    className="kiss-beso-popup-text"
                    style={{
                        left: `${b.x}px`,
                        top: `${b.y}px`,
                        '--rot': `${b.rot}deg`
                    }}
                >
                    ¡BESO!
                </div>
            ))}
            {/* Corazones flotantes alrededor del contorno del visor central */}
            {heartTensionActive && (
                <div className="kiss-visor-outline-container" style={{ position: 'absolute', top: 0, left: 0, width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: 12 }}>
                    {heartVisorOutlineParticles.map(p => (
                        <div 
                            key={p.id}
                            className="kiss-visor-outline-heart"
                            style={{
                                left: `${p.left}px`,
                                top: `${p.top}px`,
                                animationDelay: `${p.delay}s`,
                                animationDuration: `${p.duration}s`,
                                width: `${p.size}px`,
                                height: `${p.size}px`
                            }}
                        >
                            {getShapeSvg('hearts')}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export { Kiss };
export default Kiss;
